/**
 * EditShipmentForm — slide-over voor "Zending bewerken".
 *
 * Pre-loads bestaande zending in form-state, toont waarschuwingen bovenaan
 * bij gekoppelde consolidatie/factuur, hergebruikt de multi-colli/party
 * componenten uit WarehouseFormShared.
 *
 * RBAC:
 *  - Owner/Admin/Manager: alle velden bewerkbaar
 *  - Medewerker: operationele velden (colli/gewicht/notities/contact) wel,
 *    diensttype/bestemming/hub/agency/payment_status verborgen of disabled
 *  - Backend valideert opnieuw bij PATCH zodat client-side niet
 *    gemanipuleerd kan worden.
 *
 * Post-save warnings worden in een dialog getoond zodat operator handmatig
 * kan corrigeren bij gekoppelde consolidatie-mismatch.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Package as PackageIcon, User as UserIcon, MapPin, Box, Loader2, X as XIcon,
  Save, Plus, Copy, ClipboardCopy, Layers, Receipt, AlertCircle, CheckCircle2,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";

import { adminApi } from "../../../context/AuthContext";
import { ScanButton } from "./ScanButton";
import {
  SERVICE_TYPES, MEASUREMENT_LABELS, CURRENCIES,
  num, fmtNum, newColli, calcItemCbm, computeTotals, blockStatus, stripParty,
  customerToParty, isItemEmpty, itemsFromApi,
  Field, LiveSummary, Block, ColliRow, PartyFields, CustomerSearchField, WarningBanner,
} from "./WarehouseFormShared";
// P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL: override-detection
// + "Ook klantgegevens bijwerken" flow.
import { detectAddressOverrides, applyContactToParty } from "../../../services/customerAutofillService";
import AddressOverrideDialog from "../../shared/AddressOverrideDialog";
import ContactPersonSelector from "../../shared/ContactPersonSelector";
// Sprint jun 2026 — herbruikbare ontvangers + gecontroleerde documentset-sync
import RecipientAddressBookPicker, { recipientBookToParty } from "../shared/RecipientAddressBookPicker";
import DocumentSetSyncDialog from "../shared/DocumentSetSyncDialog";

function shipmentToForm(label) {
  const sd = label.shipment_details || {};
  return {
    warehouse_hub: label.warehouse_hub || "",
    agency: label.agency || "",
    service_type: label.service_type || "seafreight",
    linked_external_tracking_number: label.linked_external_tracking_number || "",
    sender: {
      name: label.sender?.name || "",
      company: label.sender?.company || "",
      phone: label.sender?.phone || "",
      whatsapp: label.sender?.whatsapp || "",
      email: label.sender?.email || "",
      street: label.sender?.street || "",
      house_number: label.sender?.house_number || "",
      house_number_addition: label.sender?.house_number_addition || "",
      postal_code: label.sender?.postal_code || "",
      city: label.sender?.city || "",
      country: label.sender?.country || "",
    },
    recipient: {
      name: label.recipient?.name || "",
      company: label.recipient?.company || "",
      phone: label.recipient?.phone || "",
      whatsapp: label.recipient?.whatsapp || "",
      email: label.recipient?.email || "",
      street: label.recipient?.street || "",
      house_number: label.recipient?.house_number || "",
      house_number_addition: label.recipient?.house_number_addition || "",
      postal_code: label.recipient?.postal_code || "",
      city: label.recipient?.city || "",
      country: label.recipient?.country || "",
    },
    customer_id: label.customer_id || null,
    destination: sd.destination || "",
    customer_note: sd.customer_note || "",
    internal_note: sd.internal_note || "",
    currency: sd.currency || "EUR",
    measurement: sd.measurement || "maritimo_cbm",
    items: itemsFromApi(sd.items),
  };
}

export default function EditShipmentForm({ open, shipment, onClose, onSaved }) {
  const [form, setForm] = useState(() => shipmentToForm(shipment));
  const [busy, setBusy] = useState(false);
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const [confirmCopyAll, setConfirmCopyAll] = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const [postSaveWarnings, setPostSaveWarnings] = useState(null);
  const [pendingSavedData, setPendingSavedData] = useState(null);

  // Reset form als andere zending wordt geopend
  useEffect(() => {
    if (open && shipment) {
      setForm(shipmentToForm(shipment));
      setPostSaveWarnings(null);
      setPendingSavedData(null);
    }
  }, [open, shipment]);

  // Laad edit-context (linked consolidation/invoice + role-fields)
  useEffect(() => {
    if (!open || !shipment?.id) return;
    let cancelled = false;
    setLoadingContext(true);
    (async () => {
      try {
        const r = await adminApi.get(`/admin/shipping-labels/${shipment.id}/edit-context`);
        if (!cancelled) setContext(r.data);
      } catch (err) {
        if (!cancelled) toast.error("Kan bewerk-context niet laden");
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, shipment?.id]);

  const isEmployee = !!context?.is_employee;
  const fields = context?.editable_fields || {};
  // Defensieve defaults wanneer context nog laadt (alles editable voor non-employee)
  const canEdit = (key) => (context ? !!fields[key] : true);

  const setSender = (patch) => setForm((f) => ({ ...f, sender: { ...f.sender, ...patch } }));
  const setRecipient = (patch) => setForm((f) => ({ ...f, recipient: { ...f.recipient, ...patch } }));

  // P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL:
  // bewaar de originele customer-payload NA elke autofill zodat we bij
  // submit kunnen detecteren welke velden de gebruiker heeft aangepast.
  // Dan tonen we de override-dialog i.p.v. stilzwijgend de klantstamkaart
  // NIET bij te werken (of, gevaarlijker, WEL bij te werken).
  const [senderSourceCustomer, setSenderSourceCustomer] = useState(null);
  const [recipientSourceCustomer, setRecipientSourceCustomer] = useState(null);
  // Batch 3 · Fase 5 — Actieve contactpersoon per party (stale-reset bij nieuwe klant).
  const [senderContactKey, setSenderContactKey] = useState("primary");
  const [recipientContactKey, setRecipientContactKey] = useState("primary");
  const [overrideDialog, setOverrideDialog] = useState(null); // {customer, diffs, party}
  // Sprint jun 2026 — adresboek-ontvanger + documentset-sync na opslaan
  const [recipientBookId, setRecipientBookId] = useState(null);
  const [saveRecipient, setSaveRecipient] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [pendingSyncData, setPendingSyncData] = useState(null);

  const status = useMemo(() => blockStatus(form), [form]);
  const allDone = status.basisDone && status.senderDone && status.recipientDone && status.contentDone;
  const completedBlocks =
    (status.basisDone ? 1 : 0) + (status.senderDone ? 1 : 0) +
    (status.recipientDone ? 1 : 0) + (status.contentDone ? 1 : 0);

  const totals = useMemo(
    () => computeTotals(form.items, form.service_type),
    [form.items, form.service_type],
  );

  // ====== Colli operations ======
  const addColli = () => setForm((f) => ({ ...f, items: [...f.items, newColli(f.items.length)] }));

  const copyPrevColli = () => {
    setForm((f) => {
      if (f.items.length === 0) return f;
      const last = f.items[f.items.length - 1];
      return {
        ...f,
        items: [...f.items, {
          ...newColli(f.items.length),
          description: last.description, quantity: last.quantity,
          weight_kg: last.weight_kg, length_cm: last.length_cm,
          width_cm: last.width_cm, height_cm: last.height_cm,
          declared_value: last.declared_value, packaging_type: last.packaging_type,
        }],
      };
    });
  };

  const copyToAll = () => {
    setForm((f) => {
      if (f.items.length === 0) return f;
      const first = f.items[0];
      const items = f.items.map((it, idx) => ({
        ...it, colli_number: idx + 1,
        description: first.description, quantity: first.quantity,
        weight_kg: first.weight_kg, length_cm: first.length_cm,
        width_cm: first.width_cm, height_cm: first.height_cm,
        declared_value: first.declared_value, packaging_type: first.packaging_type,
      }));
      return { ...f, items };
    });
    setConfirmCopyAll(false);
    toast.success("Eerste colli is naar alle colli gekopieerd");
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const deleteItem = (idx) => {
    setForm((f) => {
      const items = f.items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, colli_number: i + 1 }));
      return { ...f, items: items.length > 0 ? items : [newColli(0)] };
    });
    setConfirmDeleteIdx(null);
  };

  const requestDeleteItem = (idx) => {
    const it = form.items[idx];
    if (isItemEmpty(it)) deleteItem(idx);
    else setConfirmDeleteIdx(idx);
  };

  // ====== Submit ======
  const submit = async () => {
    if (!allDone) {
      toast.error("Vul eerst alle vier blokken volledig in.");
      return;
    }
    // P0 SPRINT (Aug 2026) — Override detectie: als een gekoppelde klant
    // is gebruikt maar de gebruiker heeft adres/contact gewijzigd, toon
    // dialog VOOR opslaan. Sender heeft prioriteit (meestal ONS bedrijf).
    const recDiffs = detectAddressOverrides(recipientSourceCustomer, form.recipient);
    const sndDiffs = detectAddressOverrides(senderSourceCustomer, form.sender);
    if (recDiffs.length > 0 || sndDiffs.length > 0) {
      const chosen = recDiffs.length > 0
        ? { customer: recipientSourceCustomer, diffs: recDiffs, party: "recipient" }
        : { customer: senderSourceCustomer, diffs: sndDiffs, party: "sender" };
      setOverrideDialog(chosen);
      return; // Wacht op user-keuze; submit wordt opnieuw gestart via `proceedSave`.
    }
    await proceedSave();
  };

  const proceedSave = async () => {
    setBusy(true);
    try {
      // Bouw alleen velden die deze rol mag wijzigen
      const payload = {};
      if (canEdit("warehouse_hub")) payload.warehouse_hub = form.warehouse_hub.trim();
      if (canEdit("agency")) payload.agency = form.agency.trim() || null;
      if (canEdit("service_type")) payload.service_type = form.service_type;
      if (canEdit("linked_external_tracking_number")) payload.linked_external_tracking_number = form.linked_external_tracking_number.trim() || null;
      payload.sender = stripParty(form.sender);
      payload.recipient = stripParty(form.recipient);
      // shipment_details — items[] is altijd toegestaan voor medewerker
      const sd = {
        items: form.items.map((it, idx) => ({
          id: it.id || undefined,
          colli_number: idx + 1,
          description: it.description?.trim() || null,
          quantity: parseInt(num(it.quantity) || 1, 10) || 1,
          weight_kg: num(it.weight_kg),
          length_cm: num(it.length_cm),
          width_cm: num(it.width_cm),
          height_cm: num(it.height_cm),
          declared_value: num(it.declared_value),
          packaging_type: it.packaging_type || null,
        })),
        currency: form.currency || "EUR",
        measurement: form.measurement,
      };
      if (canEdit("customer_note") && form.customer_note !== undefined) sd.customer_note = form.customer_note.trim() || null;
      if (canEdit("internal_note") && form.internal_note !== undefined) sd.internal_note = form.internal_note.trim() || null;
      if (canEdit("destination_country") && form.destination !== undefined) sd.destination = form.destination.trim() || null;
      payload.shipment_details = sd;
      // Herbruikbare ontvangers — de zending houdt zijn eigen snapshot.
      payload.save_recipient_to_address_book = !!(saveRecipient && form.customer_id);
      payload.recipient_book_id = recipientBookId || null;

      const r = await adminApi.patch(`/admin/shipping-labels/${shipment.id}`, payload);
      const warnings = r.data?._warnings || [];
      const syncHint = r.data?._document_set_sync;
      toast.success(`Zending ${r.data.tracking_number} bijgewerkt`);
      if (warnings.length > 0) {
        // Houd slide-over open tot gebruiker waarschuwingen heeft bevestigd
        setPostSaveWarnings(warnings);
        setPendingSavedData(r.data);
        if (syncHint?.suggested) setPendingSyncData(r.data);
      } else if (syncHint?.suggested) {
        // Alleen bij écht geraakte documenten: gecontroleerde sync-dialoog.
        setPendingSyncData(r.data);
        setSyncDialogOpen(true);
      } else {
        onSaved?.(r.data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Wijziging opslaan mislukt");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    setPostSaveWarnings(null);
    onClose?.();
  };

  if (!shipment) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-5xl p-0 flex flex-col bg-[#F4F5F7]"
        data-testid="edit-shipment-sheet"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Zending bewerken</SheetTitle>
          <SheetDescription>
            Pas de zending aan. Wijzigingen worden direct opgeslagen en
            consolidatie-totalen worden bijgewerkt.
          </SheetDescription>
        </SheetHeader>

        <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-6 py-4 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
              <PackageIcon className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h1 className="font-chivo font-black text-xl tracking-tight">Zending bewerken</h1>
              <p className="text-xs text-blue-100 mt-0.5 font-mono">
                {shipment.tracking_number}
                {isEmployee && <span className="ml-2 px-1.5 py-0.5 bg-amber-400/20 border border-amber-300/40 rounded text-amber-100">Medewerker — beperkte rechten</span>}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleClose}
                  className="text-white/70 hover:text-white"
                  data-testid="edit-shipment-close"
                  aria-label="Sluiten">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Waarschuwingen */}
        {!loadingContext && (context?.linked_consolidation || context?.linked_invoice) && (
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0 space-y-2">
            {context.linked_consolidation && (
              <WarningBanner tone="red" icon={Layers}
                             title={`Gekoppeld aan consolidatie ${context.linked_consolidation.consolidation_number}`}
                             testId="edit-warning-consolidation">
                Deze zending is al gekoppeld aan een consolidatie ({context.linked_consolidation.departure_hub_label} → {context.linked_consolidation.destination_hub_label}).
                Wijzigingen kunnen invloed hebben op totalen, bestemming of rapportages. Consolidatie-totalen worden automatisch bijgewerkt.
              </WarningBanner>
            )}
            {context.linked_invoice && (
              <WarningBanner tone="amber" icon={Receipt}
                             title={`Gekoppelde factuur ${context.linked_invoice.invoice_number}`}
                             testId="edit-warning-invoice">
                Deze zending heeft een gekoppelde factuur ({context.linked_invoice.currency} {context.linked_invoice.total_incl_vat}).
                Controleer of de factuur nog klopt na deze wijziging. De factuur wordt niet automatisch aangepast.
              </WarningBanner>
            )}
          </div>
        )}

        <LiveSummary
          completed={completedBlocks}
          totals={totals}
          currency={form.currency}
          allDone={allDone}
          testIdPrefix="edit-live"
        />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <Block index={1} title="Zending-basis"
                 description={isEmployee ? "Diensttype/hub kunnen niet door medewerker worden gewijzigd" : "Magazijn, agentschap en diensttype"}
                 icon={PackageIcon} done={status.basisDone}
                 testId="warehouse-block-basis">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={`Magazijn / hub * ${!canEdit("warehouse_hub") ? "(geen rechten)" : ""}`}>
                <Input value={form.warehouse_hub}
                       onChange={(e) => setForm((f) => ({ ...f, warehouse_hub: e.target.value }))}
                       disabled={!canEdit("warehouse_hub")}
                       placeholder="Rotterdam HQ" className="rounded-none h-10"
                       data-testid="warehouse-field-hub" />
              </Field>
              <Field label={`Agentschap ${!canEdit("agency") ? "(geen rechten)" : ""}`}>
                <Input value={form.agency}
                       onChange={(e) => setForm((f) => ({ ...f, agency: e.target.value }))}
                       disabled={!canEdit("agency")}
                       placeholder="5999Cargo NL" className="rounded-none h-10"
                       data-testid="warehouse-field-agency" />
              </Field>
              <Field label={`Diensttype * ${!canEdit("service_type") ? "(geen rechten)" : ""}`}>
                <Select value={form.service_type}
                        onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
                        disabled={!canEdit("service_type")}>
                  <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`warehouse-service-${s.key}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Extern W-nummer">
                <div className="flex gap-2">
                  <Input value={form.linked_external_tracking_number}
                         onChange={(e) => setForm((f) => ({ ...f, linked_external_tracking_number: e.target.value.toUpperCase() }))}
                         placeholder="W523518422761"
                         className="rounded-none h-10 font-mono text-xs uppercase flex-1"
                         disabled={!canEdit("linked_external_tracking_number")}
                         data-testid="warehouse-field-external-w" />
                  {canEdit("linked_external_tracking_number") && (
                    <ScanButton iconOnly testId="warehouse-scan-external-w"
                                title="Scan extern W-nummer"
                                onResult={(v) => setForm((f) => ({ ...f, linked_external_tracking_number: v.toUpperCase() }))} />
                  )}
                </div>
              </Field>
            </div>
          </Block>

          <Block index={2} title="Afzender"
                 description="Klantkoppeling optioneel"
                 icon={UserIcon} done={status.senderDone}
                 testId="warehouse-block-sender">
            {!isEmployee && (
              <CustomerSearchField testIdPrefix="warehouse-sender-customer"
                                   onSelect={(c) => {
                                     setForm((f) => ({ ...f, customer_id: c.id || c.customer_id }));
                                     setSender(customerToParty(c));
                                     setSenderSourceCustomer(c);
                                     setSenderContactKey("primary");
                                     toast.success(`Klant gekoppeld als afzender`);
                                   }} />
            )}
            {!isEmployee && (
              <ContactPersonSelector
                customer={senderSourceCustomer}
                value={senderContactKey}
                onSelect={(key, contact) => {
                  setSenderContactKey(key);
                  setForm((f) => ({ ...f, sender: applyContactToParty(f.sender, contact) }));
                }}
                label="Contactpersoon (afzender)"
                testIdPrefix="warehouse-sender-contact"
              />
            )}
            <PartyFields party={form.sender} onChange={setSender}
                         testIdPrefix="warehouse-sender" />
          </Block>

          <Block index={3} title="Ontvanger"
                 description={isEmployee ? "Land en stad kunnen niet door medewerker worden gewijzigd" : "Bestemming bepaalt consolidatie-validatie"}
                 icon={MapPin} done={status.recipientDone}
                 testId="warehouse-block-recipient">
            {!isEmployee && (
              <CustomerSearchField testIdPrefix="warehouse-recipient-customer"
                                   onSelect={(c) => {
                                     setForm((f) => ({ ...f, customer_id: c.id || c.customer_id }));
                                     setRecipient(customerToParty(c));
                                     setRecipientSourceCustomer(c);
                                     setRecipientContactKey("primary");
                                     toast.success(`Klant gekoppeld als ontvanger`);
                                   }} />
            )}
            {!isEmployee && (
              <ContactPersonSelector
                customer={recipientSourceCustomer}
                value={recipientContactKey}
                onSelect={(key, contact) => {
                  setRecipientContactKey(key);
                  setForm((f) => ({ ...f, recipient: applyContactToParty(f.recipient, contact) }));
                }}
                label="Contactpersoon (ontvanger)"
                testIdPrefix="warehouse-recipient-contact"
              />
            )}
            {/* Sprint jun 2026 — opgeslagen ontvangers van deze klant.
                Alleen voor rollen die land/stad mogen wijzigen, anders zou een
                adresboek-keuze een 403 opleveren bij opslaan. */}
            {!isEmployee && (
              <div className="mt-2">
                <RecipientAddressBookPicker
                  customerId={form.customer_id}
                  testIdPrefix="warehouse-recipient-book"
                  onSelect={(r) => {
                    setRecipient(recipientBookToParty(r));
                    setRecipientBookId(r.id);
                    setRecipientSourceCustomer(null);
                    toast.success(`Ontvanger ${r.name} ingevuld uit adresboek`);
                  }}
                />
              </div>
            )}
            <PartyFields party={form.recipient} onChange={setRecipient}
                         testIdPrefix="warehouse-recipient"
                         nameRequired
                         disabledCountryCity={!canEdit("recipient_country_city")} />
            {form.customer_id && !isEmployee && (
              <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer"
                     data-testid="warehouse-recipient-save-label">
                <input type="checkbox" checked={saveRecipient} className="mt-0.5"
                       onChange={(e) => setSaveRecipient(e.target.checked)}
                       data-testid="warehouse-recipient-save" />
                <span>
                  <strong>Ontvanger bewaren</strong> in het adresboek van deze klant —
                  volgende keer direct te kiezen. Deze zending houdt altijd zijn
                  eigen gegevens.
                </span>
              </label>
            )}
            <div className="mt-3">
              <Field label={`Bestemming (vrij tekst) ${!canEdit("destination_country") ? "(geen rechten)" : ""}`}>
                <Input value={form.destination}
                       onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                       disabled={!canEdit("destination_country")}
                       placeholder="Willemstad, Curaçao" className="rounded-none h-10"
                       data-testid="warehouse-field-destination" />
              </Field>
            </div>
          </Block>

          <Block index={4} title="Colli & inhoud"
                 description={`${form.items.length} colli · CBM en gewicht worden live berekend`}
                 icon={Box} done={status.contentDone}
                 testId="warehouse-block-content">
            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <ColliRow key={it._local_id || idx}
                          item={it} index={idx}
                          serviceType={form.service_type}
                          onChange={(patch) => updateItem(idx, patch)}
                          onDelete={() => requestDeleteItem(idx)}
                          showDelete={form.items.length > 1 || !isItemEmpty(it)} />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
              <Button type="button" variant="outline" size="sm"
                      onClick={addColli}
                      className="rounded-none text-xs h-9"
                      data-testid="warehouse-add-colli">
                <Plus className="w-3.5 h-3.5 mr-1" /> Colli toevoegen
              </Button>
              <Button type="button" variant="outline" size="sm"
                      onClick={copyPrevColli}
                      disabled={form.items.length === 0}
                      className="rounded-none text-xs h-9"
                      data-testid="warehouse-copy-prev-colli">
                <Copy className="w-3.5 h-3.5 mr-1" /> Vorige colli kopiëren
              </Button>
              <Button type="button" variant="outline" size="sm"
                      onClick={() => setConfirmCopyAll(true)}
                      disabled={form.items.length < 2}
                      className="rounded-none text-xs h-9"
                      data-testid="warehouse-copy-to-all">
                <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Kopieer naar alle colli
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Meting">
                <Select value={form.measurement} onValueChange={(v) => setForm((f) => ({ ...f, measurement: v }))}>
                  <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-measurement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEASUREMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Field label="Klantnotitie">
                <Textarea rows={2} value={form.customer_note}
                          onChange={(e) => setForm((f) => ({ ...f, customer_note: e.target.value }))}
                          disabled={!canEdit("customer_note")}
                          className="rounded-none"
                          data-testid="warehouse-field-customer-note" />
              </Field>
              <Field label="Interne notitie">
                <Textarea rows={2} value={form.internal_note}
                          onChange={(e) => setForm((f) => ({ ...f, internal_note: e.target.value }))}
                          disabled={!canEdit("internal_note")}
                          className="rounded-none"
                          data-testid="warehouse-field-internal-note" />
              </Field>
            </div>
          </Block>

          <div className="pb-2" />
        </div>

        <div className="border-t border-gray-200 bg-white px-4 sm:px-6 py-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between flex-shrink-0">
          <div className="text-xs text-gray-500" data-testid="edit-shipment-block-status">
            {completedBlocks} van 4 blokken compleet
            {allDone ? (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" /> Klaar om op te slaan
              </span>
            ) : (
              <span className="ml-2 text-amber-700">
                — nog nodig: {[
                  !status.basisDone && "Basis (hub + dienst)",
                  !status.senderDone && "Afzender (naam + telefoon/e-mail)",
                  !status.recipientDone && "Ontvanger (naam)",
                  !status.contentDone && "Colli (elke colli gewicht + L\u00d7B\u00d7H)",
                ].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
          <div className="flex w-full sm:w-auto min-w-0 items-center gap-2">
            <Button variant="outline" onClick={handleClose} disabled={busy}
                    className="rounded-none text-xs h-10"
                    data-testid="edit-shipment-cancel">
              Annuleren
            </Button>
            <Button onClick={submit} disabled={busy || !allDone}
                    className="flex-1 sm:flex-none min-w-0 bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none text-xs font-bold uppercase h-10 px-3 sm:px-5"
                    data-testid="edit-shipment-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {busy ? "Opslaan…" : "Wijzigingen opslaan"}
            </Button>
          </div>
        </div>
      </SheetContent>

      <AlertDialog open={confirmCopyAll} onOpenChange={(o) => !o && setConfirmCopyAll(false)}>
        <AlertDialogContent data-testid="warehouse-confirm-copy-all">
          <AlertDialogHeader>
            <AlertDialogTitle>Eerste colli naar alle colli kopiëren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit overschrijft de huidige waarden van alle colli-regels.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="warehouse-confirm-copy-all-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={copyToAll} data-testid="warehouse-confirm-copy-all-ok">
              Ja, kopieer naar alle colli
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeleteIdx !== null} onOpenChange={(o) => !o && setConfirmDeleteIdx(null)}>
        <AlertDialogContent data-testid="warehouse-confirm-delete-colli">
          <AlertDialogHeader>
            <AlertDialogTitle>Colli verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet u zeker dat u Colli {(confirmDeleteIdx ?? 0) + 1} wilt verwijderen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="warehouse-confirm-delete-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteItem(confirmDeleteIdx)} data-testid="warehouse-confirm-delete-ok">
              Ja, verwijder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {postSaveWarnings && (
        <AlertDialog open={true} onOpenChange={(o) => !o && setPostSaveWarnings(null)}>
          <AlertDialogContent data-testid="edit-shipment-post-save-warnings">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" /> Wijziging opgeslagen met waarschuwing
              </AlertDialogTitle>
              <AlertDialogDescription>
                De zending is bijgewerkt, maar er zijn punten die jouw aandacht vragen:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="text-sm space-y-2 my-2">
              {postSaveWarnings.map((w, i) => (
                <li key={i} className="bg-amber-50 border border-amber-200 px-3 py-2 rounded text-amber-900">
                  {w.message}
                </li>
              ))}
            </ul>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={() => {
                  const saved = pendingSavedData;
                  setPostSaveWarnings(null);
                  setPendingSavedData(null);
                  // Documentset-sync komt ná de waarschuwingen aan de beurt.
                  if (pendingSyncData) {
                    setSyncDialogOpen(true);
                    return;
                  }
                  if (saved) onSaved?.(saved);
                }}
                data-testid="edit-shipment-warnings-ok">
                Begrepen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Sprint jun 2026 — gecontroleerde documentset-sync na opslaan.
          Verschijnt uitsluitend wanneer de wijziging documenten raakt die
          daadwerkelijk bijgewerkt kunnen worden. */}
      {syncDialogOpen && pendingSyncData && (
        <DocumentSetSyncDialog
          open={syncDialogOpen}
          shipmentId={shipment.id}
          onClose={() => {
            const saved = pendingSyncData;
            setSyncDialogOpen(false);
            setPendingSyncData(null);
            onSaved?.(saved);
          }}
        />
      )}

      {/* P0 SPRINT (Aug 2026) — Override confirm dialog na klant-autofill */}
      {overrideDialog && (
        <AddressOverrideDialog
          open={!!overrideDialog}
          onOpenChange={(v) => { if (!v) setOverrideDialog(null); }}
          customerId={overrideDialog.customer?.customer_id || overrideDialog.customer?.id}
          customerName={overrideDialog.customer?.display_name || overrideDialog.customer?.company_name}
          diffs={overrideDialog.diffs}
          onKeepShipmentOnly={async () => {
            // Bewaar shipment override zonder klantstamkaart aan te passen
            const wasRec = overrideDialog.party === "recipient";
            setOverrideDialog(null);
            // Clear source zodat volgende submit niet opnieuw dialog triggert
            if (wasRec) setRecipientSourceCustomer(null);
            else setSenderSourceCustomer(null);
            await proceedSave();
          }}
        />
      )}
    </Sheet>
  );
}
